import { motion } from 'motion/react';
import { Link } from '@tanstack/react-router';
import { useSavePendingQuiz, useDeletePendingQuiz } from '@/hooks/usePendingQuizzes';
import { useToast } from '@/contexts/ToastContext';
import type { PendingQuiz } from '@/types';
import styles from './PendingQuizRow.module.scss';

interface PendingQuizRowProps {
  quiz: PendingQuiz;
  index: number;
}

export function PendingQuizRow({ quiz, index }: PendingQuizRowProps) {
  const { showToast } = useToast();
  const saveMutation = useSavePendingQuiz();
  const deleteMutation = useDeletePendingQuiz();

  const displayName = quiz.quiz_name || truncateText(quiz.prompt, 50);
  const timeAgo = formatDate(new Date(quiz.created_at));
  const isReady = quiz.status === 'ready';
  const isFailed = quiz.status === 'failed';
  const isProcessing = quiz.status === 'processing';

  const handleSave = async () => {
    try {
      await saveMutation.mutateAsync({ id: quiz.id });
      showToast('Quiz saved to your collection!', 'success');
    } catch {
      showToast('Failed to save quiz. Please try again.', 'error');
    }
  };

  const handleDiscard = async () => {
    try {
      await deleteMutation.mutateAsync(quiz.id);
      showToast('Quiz discarded', 'info');
    } catch {
      showToast('Failed to discard quiz. Please try again.', 'error');
    }
  };

  const questionCount = quiz.quiz_data?.questions?.length ?? 0;

  return (
    <motion.div
      className={`${styles.row} ${styles[`row--${quiz.status}`]}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
    >
      <div className={styles.row__content}>
        <div className={styles.row__main}>
          <div className={styles.row__status}>
            <StatusIndicator status={quiz.status} />
          </div>
          <div className={styles.row__info}>
            <h3 className={styles.row__name}>{displayName}</h3>
            <p className={styles.row__prompt}>{quiz.prompt}</p>
            <div className={styles.row__meta}>
              <span className={styles.row__metaItem}>
                {questionCount > 0 ? `${questionCount} questions` : 'Generating...'}
              </span>
              <span className={styles.row__metaDivider}>|</span>
              <span className={styles.row__metaItem}>{timeAgo}</span>
              {isFailed && quiz.error_message && (
                <>
                  <span className={styles.row__metaDivider}>|</span>
                  <span className={`${styles.row__metaItem} ${styles['row__metaItem--error']}`}>
                    {quiz.error_message}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        <div className={styles.row__actions}>
          {isReady && (
            <>
              <Link
                to={`/review-center/${quiz.id}/review` as any}
                className={`${styles.row__button} ${styles['row__button--secondary']}`}
              >
                Review
              </Link>
              <Link
                to={`/review-center/${quiz.id}/practice` as any}
                className={`${styles.row__button} ${styles['row__button--secondary']}`}
              >
                Practice
              </Link>
              <button
                className={`${styles.row__button} ${styles['row__button--primary']}`}
                onClick={handleSave}
                disabled={saveMutation.isPending}
              >
                {saveMutation.isPending ? 'Saving...' : 'Save'}
              </button>
            </>
          )}
          {isProcessing && (
            <span className={styles.row__processingText}>Generating quiz...</span>
          )}
          <button
            className={`${styles.row__button} ${styles['row__button--danger']}`}
            onClick={handleDiscard}
            disabled={deleteMutation.isPending}
            title={isProcessing ? 'Cancel generation' : 'Discard quiz'}
          >
            {deleteMutation.isPending ? 'Discarding...' : isProcessing ? 'Cancel' : 'Discard'}
          </button>
        </div>
      </div>
    </motion.div>
  );
}

function StatusIndicator({ status }: { status: PendingQuiz['status'] }) {
  return (
    <div className={`${styles.statusIndicator} ${styles[`statusIndicator--${status}`]}`}>
      {status === 'processing' && <SpinnerIcon />}
      {status === 'ready' && <CheckIcon />}
      {status === 'failed' && <ErrorIcon />}
    </div>
  );
}

function SpinnerIcon() {
  return (
    <svg className={styles.statusIndicator__spinner} width="20" height="20" viewBox="0 0 20 20">
      <circle
        cx="10"
        cy="10"
        r="7"
        stroke="currentColor"
        strokeWidth="2"
        fill="none"
        strokeDasharray="32"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.5" />
      <path d="M6 10L9 13L14 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ErrorIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.5" />
      <path d="M7 7L13 13M13 7L7 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + '...';
}

function formatDate(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;

  return date.toLocaleDateString();
}
